import {
  ArrowClockwiseIcon,
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CaretUpIcon,
  ChatCircleIcon,
  CodeIcon,
  HeartIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { CodeFile, type EditorError } from 'mini-code'
import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type Comment, type Track } from './api.ts'
import { useAuth } from './AuthContext.tsx'
import { BytecodeCompiler } from './bytecode-compiler.ts'
import {
  type AmplitudeInfo,
  type LevelInfo,
  type NumberWithParamsInfo,
  type PatternEventInfo,
  type SequenceData,
  type TimelineInfo,
  type WaveformInfo,
} from './bytecode.ts'
import { dspscript } from './dsp-script.ts'
import { adjustErrorLineNumbers, convertToEditorErrors } from './editor-errors.ts'
import { InlineEditor } from './InlineEditor.tsx'
import { ProfileImage } from './ProfileImage.tsx'
import { Link } from './Router.tsx'
import { Spinner } from './Spinner.tsx'
import { useChannelModeStore, useGlobalBpmStore, useLatencyHintStore, useSessionStore } from './store.ts'
import { formatRelativeTime } from './util.ts'

interface TrackPlayerProps {
  track: Track
  isActive: boolean
  showTitle?: boolean
  onPlay: () => void
  onStop: () => void
  onPlayingChange: (isPlaying: boolean) => void
  onLoad: (track: Track) => void
  onLoadInA?: (track: Track) => void
  onLoadInB?: (track: Track) => void
  onToggleLike: (track: Track) => void
  isLiked: boolean
  parentTrack?: Track
  remixes: Track[]
  onPlayRemix: (remix: Track) => void
  onStopRemix: () => void
  activeRemixId: string | null
  likedTrackIds: Set<string>
  channelMode?: 'single' | 'dual'
  compileMode: 'key' | 'ctrl+enter'
  expandCommentsByDefault?: boolean
  onOpenAuthModal?: (track?: Track) => void
  actuallyPlaying?: boolean
}

export function TrackPlayer({
  track,
  isActive,
  showTitle = true,
  onPlay,
  onStop,
  onPlayingChange,
  onLoad,
  onLoadInA,
  onLoadInB,
  onToggleLike,
  isLiked,
  parentTrack,
  remixes,
  onPlayRemix,
  onStopRemix,
  activeRemixId,
  likedTrackIds,
  channelMode = 'single',
  compileMode,
  expandCommentsByDefault = false,
  onOpenAuthModal,
  actuallyPlaying: actuallyPlayingProp,
}: TrackPlayerProps) {
  const { user } = useAuth()
  const { latencyHint } = useLatencyHintStore()
  const { removeEditorInitiatedPlayback, removeEditorPlayingTabId } = useSessionStore()
  const params = new URLSearchParams(window.location.search)
  const hasNoAccount = params.has('noaccount')
  const compilerRef = useRef(new BytecodeCompiler())
  const [isPlaying, setIsPlaying] = useState(false)
  const [actuallyPlaying, setActuallyPlaying] = useState(false)
  const [error, setError] = useState('')
  const [editorErrors, setEditorErrors] = useState<EditorError[]>([])
  const [patternEvents, setPatternEvents] = useState<PatternEventInfo[]>([])
  const [sequences, setSequences] = useState<SequenceData[]>([])
  const [numbersWithParams, setNumbersWithParams] = useState<NumberWithParamsInfo[]>([])
  const [amplitudeInfos, setAmplitudeInfos] = useState<AmplitudeInfo[]>([])
  const [waveformInfos, setWaveformInfos] = useState<WaveformInfo[]>([])
  const [levelInfos, setLevelInfos] = useState<LevelInfo[]>([])
  const [timelineInfos, setTimelineInfos] = useState<TimelineInfo[]>([])
  const [showRemixes, setShowRemixes] = useState(false)
  const [showComments, setShowComments] = useState(expandCommentsByDefault)

  useEffect(() => {
    if (expandCommentsByDefault) {
      setShowComments(true)
    }
  }, [expandCommentsByDefault])
  const [currentDisplayedId, setCurrentDisplayedId] = useState<string>(track.id)
  const [comments, setComments] = useState<Comment[]>([])
  const [commentText, setCommentText] = useState('')
  const [loadingComments, setLoadingComments] = useState(false)
  const [postingComment, setPostingComment] = useState(false)
  const [commentsExpanded, setCommentsExpanded] = useState(false)
  const codeFilesRef = useRef<Map<string, CodeFile>>(new Map())
  const commentInputRef = useRef<HTMLInputElement>(null)
  const prevShowCommentsRef = useRef<boolean>(expandCommentsByDefault)
  const hasAutoOpenedCommentsRef = useRef<boolean>(false)
  const hasManuallyToggledCommentsRef = useRef<boolean>(false)

  const getCodeFile = useCallback((id: string, initialCode: string): CodeFile => {
    if (!codeFilesRef.current.has(id)) {
      codeFilesRef.current.set(id, new CodeFile(initialCode))
    }
    return codeFilesRef.current.get(id)!
  }, [])

  const getInitialCode = useCallback(
    (id: string): string => {
      if (id === track.id) return track.code
      if (id === parentTrack?.id) return parentTrack.code
      return remixes.find(r => r.id === id)?.code || track.code
    },
    [track.id, track.code, parentTrack?.id, parentTrack?.code, remixes],
  )

  const displayedCodeFile = getCodeFile(currentDisplayedId, getInitialCode(currentDisplayedId))

  const prevDisplayedIdRef = useRef<string>(currentDisplayedId)
  const prevTrackIdRef = useRef<string>(track.id)

  useEffect(() => {
    if (prevDisplayedIdRef.current !== currentDisplayedId) {
      const codeFile = codeFilesRef.current.get(currentDisplayedId)
      if (codeFile) {
        const initialCode = getInitialCode(currentDisplayedId)
        codeFile.value = initialCode
      }
      prevDisplayedIdRef.current = currentDisplayedId
    }
  }, [currentDisplayedId, getInitialCode])

  useEffect(() => {
    if (prevTrackIdRef.current !== track.id) {
      // Reset codeFile when track changes to ensure we use the original track code
      const codeFile = codeFilesRef.current.get(track.id)
      if (codeFile) {
        codeFile.value = track.code
      }
      prevTrackIdRef.current = track.id
    }
  }, [track.id, track.code])

  // Extract pattern events, sequences, and waveform infos when displayed code changes
  useEffect(() => {
    const code = displayedCodeFile.value
    try {
      const program = compilerRef.current.compile(code)
      setPatternEvents(program.patternEvents || [])
      setSequences(program.sequences || [])
      setAmplitudeInfos(program.amplitudeInfos || [])
      setWaveformInfos(program.waveformInfos || [])
      setLevelInfos(program.levelInfos || [])
    }
    catch {
      // If compilation fails, clear pattern events, sequences, and waveform infos
      setPatternEvents([])
      setSequences([])
      setAmplitudeInfos([])
      setWaveformInfos([])
      setLevelInfos([])
    }
  }, [displayedCodeFile.value, currentDisplayedId])

  useEffect(() => {
    if (!isActive) {
      if (isPlaying) {
        setIsPlaying(false)
        onPlayingChange(false)
      }
    }
  }, [isActive, isPlaying, onPlayingChange])

  useEffect(() => {
    if (!isActive) {
      setActuallyPlaying(false)
      return
    }

    // If actuallyPlaying prop is provided, use it (from useTrackList when NavBar play is clicked)
    if (actuallyPlayingProp !== undefined) {
      setActuallyPlaying(actuallyPlayingProp)
    }
    else {
      // Check immediately when track becomes active
      const actualPlaying = dspscript.isPlaying()
      setActuallyPlaying(actualPlaying)
      if (actualPlaying !== isPlaying) {
        setIsPlaying(actualPlaying)
        onPlayingChange(actualPlaying)
      }
    }

    const interval = setInterval(() => {
      const actualPlaying = dspscript.isPlaying()
      // Only update from polling if prop is not provided, otherwise trust the prop
      if (actuallyPlayingProp === undefined) {
        setActuallyPlaying(actualPlaying)
      }
      else {
        setActuallyPlaying(actuallyPlayingProp)
      }
      if (actualPlaying !== isPlaying) {
        setIsPlaying(actualPlaying)
        onPlayingChange(actualPlaying)
      }
    }, 100)

    return () => clearInterval(interval)
  }, [isActive, isPlaying, onPlayingChange, actuallyPlayingProp])

  const handlePlay = async () => {
    try {
      setError('')
      setEditorErrors([])
      removeEditorInitiatedPlayback()
      removeEditorPlayingTabId()

      if (!dspscript.getAudioContext()) {
        await dspscript.init(latencyHint)
      }

      // Use the current edited code from the displayed codeFile
      const codeToPlay = displayedCodeFile.value

      // Ensure global BPM is synced before loading program
      const currentGlobalBpm = useGlobalBpmStore.getState().globalBpm
      dspscript.setGlobalBpm(currentGlobalBpm)

      // Extract pattern events, sequences, numbers with params, and waveform infos from compilation
      try {
        const program = compilerRef.current.compile(codeToPlay, currentGlobalBpm)
        setPatternEvents(program.patternEvents || [])
        setSequences(program.sequences || [])
        setNumbersWithParams(program.numbersWithParams || [])
        setAmplitudeInfos(program.amplitudeInfos || [])
        setWaveformInfos(program.waveformInfos || [])
        setLevelInfos(program.levelInfos || [])
        setTimelineInfos(program.timelineInfos || [])
      }
      catch (err: any) {
        console.error(err)
        // If compilation fails, clear pattern events, sequences, numbers with params, and waveform infos
        setPatternEvents([])
        setSequences([])
        setNumbersWithParams([])
        setAmplitudeInfos([])
        setWaveformInfos([])
        setLevelInfos([])
        setTimelineInfos([])
      }

      await dspscript.loadNewProgram(codeToPlay)
      await dspscript.start()
      setIsPlaying(true)
      onPlayingChange(true)

      // Call onPlay after program is loaded
      await onPlay()

      // Poll until audio actually starts playing (after samples are loaded and worklet has started)
      // This ensures the visualizer only starts when audio is actually playing
      const maxAttempts = 50
      let attempts = 0
      const checkPlaying = () => {
        // Only continue if this track is still active
        if (!isActive) {
          return
        }
        if (dspscript.isPlaying()) {
          setActuallyPlaying(true)
        }
        else if (attempts < maxAttempts) {
          attempts++
          setTimeout(checkPlaying, 20)
        }
      }
      checkPlaying()
    }
    catch (err: any) {
      console.error(err)
      const errors = convertToEditorErrors(err, displayedCodeFile.value)
      setEditorErrors(errors)
      setError(adjustErrorLineNumbers(err.message))
      setIsPlaying(false)
      setActuallyPlaying(false)
      onPlayingChange(false)
    }
  }

  const handleStop = async () => {
    try {
      await dspscript.stop()
      setIsPlaying(false)
      setActuallyPlaying(false)
      onPlayingChange(false)
      onStop()
    }
    catch (err: any) {
      console.error(err)
      const errors = convertToEditorErrors(err, displayedCodeFile.value)
      setEditorErrors(errors)
      setError(adjustErrorLineNumbers(err.message))
    }
  }

  const handleCompileCheck = useCallback((code: string) => {
    try {
      const currentGlobalBpm = useGlobalBpmStore.getState().globalBpm
      const program = compilerRef.current.compile(code, currentGlobalBpm)
      setEditorErrors([])
      setError('')
      setPatternEvents(program.patternEvents || [])
      setSequences(program.sequences || [])
      setNumbersWithParams(program.numbersWithParams || [])
      setAmplitudeInfos(program.amplitudeInfos || [])
      setWaveformInfos(program.waveformInfos || [])
    }
    catch (err: any) {
      console.error(err)
      const errors = convertToEditorErrors(err, code)
      setEditorErrors(errors)
      setError(adjustErrorLineNumbers(err.message))
      setPatternEvents([])
      setSequences([])
      setNumbersWithParams([])
    }
  }, [])

  const handleCompile = useCallback(
    async (code: string) => {
      // Only reload program if this track is active and playing
      if (isActive && isPlaying && dspscript.getAudioContext()) {
        try {
          // Ensure global BPM is synced before loading program
          const currentGlobalBpm = useGlobalBpmStore.getState().globalBpm
          dspscript.setGlobalBpm(currentGlobalBpm)
          // Use hot-reload when already playing to preserve state
          await dspscript.loadProgram(code)
          setEditorErrors([])
          setError('')
        }
        catch (err: any) {
          console.error(err)
          const errors = convertToEditorErrors(err, code)
          setEditorErrors(errors)
          setError(adjustErrorLineNumbers(err.message))
        }
      }
    },
    [isActive, isPlaying],
  )

  useEffect(() => {
    if (
      (track.commentsCount || 0) > 0
      && !showComments
      && !hasAutoOpenedCommentsRef.current
      && !hasManuallyToggledCommentsRef.current
    ) {
      setShowComments(true)
      hasAutoOpenedCommentsRef.current = true
    }
  }, [track.commentsCount, showComments])

  useEffect(() => {
    if (showComments) {
      loadComments()
      const wasManuallyExpanded = !prevShowCommentsRef.current && showComments && hasManuallyToggledCommentsRef.current
      if (wasManuallyExpanded) {
        setTimeout(() => {
          commentInputRef.current?.focus()
        }, 100)
      }
      prevShowCommentsRef.current = showComments
    }
    else {
      prevShowCommentsRef.current = false
    }
  }, [showComments])

  const loadComments = async () => {
    try {
      setLoadingComments(true)
      const response = await api.tracks.getComments(track.id)
      const fetchedComments = response.comments
      setComments(fetchedComments || [])
    }
    catch (err: any) {
      console.error(err)
      console.error('Failed to load comments:', err)
      setComments([])
    }
    finally {
      setLoadingComments(false)
    }
  }

  const handlePostComment = async () => {
    if (!commentText.trim() || postingComment) return

    try {
      setPostingComment(true)
      const { comment } = await api.tracks.postComment(track.id, commentText.trim())
      setComments([...(comments || []), comment])
      setCommentText('')
      track.commentsCount = (track.commentsCount || 0) + 1
      setCommentsExpanded(true)
      setShowComments(true)
    }
    catch (err: any) {
      console.error('Failed to post comment:', err)
    }
    finally {
      setPostingComment(false)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Are you sure you want to delete this comment?')) {
      return
    }

    try {
      await api.tracks.deleteComment(track.id, commentId)
      setComments((comments || []).filter(c => c.id !== commentId))
      track.commentsCount = Math.max(0, (track.commentsCount || 0) - 1)
    }
    catch (err: any) {
      console.error('Failed to delete comment:', err)
    }
  }

  const headerContent = (
    <div
      className="flex flex-row flex-wrap items-center justify-between gap-6 w-full"
      style={{ textShadow: '1px 1px 0 #000' }}
    >
      <div className="flex gap-3 flex-1 items-center">
        <div className="flex gap-2" onClick={e => e.stopPropagation()}>
          {channelMode === 'dual' && onLoadInA && onLoadInB
            ? (
              <>
                <button
                  onClick={() => {
                    const codeFile = getCodeFile(track.id, track.code)
                    onLoadInA({ ...track, code: codeFile.value })
                  }}
                  className="hidden md:flex pr-3 pl-2 h-8 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 items-center justify-center gap-0"
                  title="Load in A"
                >
                  <CaretLeftIcon weight="regular" size={16} />
                  <span className="text-[10px] leading-none -ml-0.5 relative top-[.65px]">A</span>
                </button>
                <button
                  onClick={() => {
                    const codeFile = getCodeFile(track.id, track.code)
                    onLoadInB({ ...track, code: codeFile.value })
                  }}
                  className="hidden md:flex pr-2 pl-3 h-8 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 items-center justify-center gap-0"
                  title="Load in B"
                >
                  <span className="text-[10px] leading-none -mr-0.5 relative top-[.65px]">B</span>
                  <CaretRightIcon weight="regular" size={16} />
                </button>
              </>
            )
            : (
              <button
                onClick={() => {
                  const codeFile = getCodeFile(track.id, track.code)
                  onLoad({ ...track, code: codeFile.value })
                }}
                className="hidden md:flex px-3 h-8 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 items-center"
                title="Load track in editor"
              >
                <CaretLeftIcon weight="regular" size={16} />
              </button>
            )}
        </div>
        {showTitle && (
          <div className="flex font-semibold text-normal items-center gap-x-1 min-w-0 w-full md:w-auto flex-wrap">
            {track.userId && (
              <Link
                href={`/profile/${track.userId}`}
                className="flex items-center gap-1 text-[#888] hover:text-orange-500"
                title={`Visit ${track.userName || 'Unknown'}'s profile`}
              >
                <ProfileImage
                  userId={track.userId}
                  userName={track.userName || 'Unknown'}
                  size={16}
                  hasAvatar={track.userHasAvatar}
                  className="shrink-0"
                />
                <span className="cursor-pointer shrink-0 md:shrink-0 break-words">
                  {track.userName || 'Unknown'}
                </span>
              </Link>
            )}
            <span className="text-[#888]">-</span>
            <span
              className="hover:text-orange-500 cursor-pointer whitespace-normal md:whitespace-nowrap min-w-0"
              title={`View ${track.title} by ${track.userName || 'Unknown'}`}
            >
              <Link href={`/loop/${track.id}`} className="break-words">
                {track.title}
              </Link>
            </span>
          </div>
        )}
        <div className="flex -ml-1.5 flex-wrap md:flex-nowrap items-center gap-1">
          {!hasNoAccount && (
            <div className="text-xs text-[#888] flex items-center gap-2 shrink-0">
              {user && track.userId === user.id && track.likesCount > 0
                ? (
                  <span className="flex items-center gap-1">
                    <HeartIcon weight="regular" size={16} />
                    <span>{track.likesCount}</span>
                  </span>
                )
                : (
                  track.userId !== user?.id && (
                    <button
                      onClick={() => {
                        if (!user) {
                          onOpenAuthModal?.(track)
                        }
                        else {
                          onToggleLike(track)
                        }
                      }}
                      className="px-2 h-5 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:opacity-90 flex items-center gap-1"
                      title={isLiked ? 'Unlike' : 'Like'}
                    >
                      <HeartIcon
                        weight="fill"
                        size={12}
                        className={isLiked ? 'text-red-500' : 'text-neutral-600'}
                      />
                      <span>{track.likesCount}</span>
                    </button>
                  )
                )}
            </div>
          )}
          <div className="text-xs text-[#888] flex items-center gap-2 shrink-0">
            {remixes.length > 0 && (
              <button
                title={showRemixes ? 'Hide Remixes' : 'Show Remixes'}
                onClick={e => {
                  e.stopPropagation()
                  setShowRemixes(!showRemixes)
                }}
                className="px-2 h-5 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:opacity-90 flex items-center gap-1"
              >
                <ArrowClockwiseIcon weight="fill" size={12} />
                <span>{remixes.length}</span>
              </button>
            )}
          </div>
          <div className="text-xs text-[#888] flex items-center gap-2 shrink-0">
            <button
              onClick={e => {
                e.stopPropagation()
                if (expandCommentsByDefault) {
                  commentInputRef.current?.focus()
                }
                else {
                  hasManuallyToggledCommentsRef.current = true
                  setShowComments(!showComments)
                }
              }}
              className="px-2 h-5 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:opacity-90 flex items-center gap-1"
              title={expandCommentsByDefault ? 'Focus comment input' : 'Comments'}
            >
              <ChatCircleIcon weight="regular" size={12} />
              <span>{track.commentsCount || 0}</span>
            </button>
          </div>
          {(remixes.length > 0 || parentTrack) && (
            <button
              onClick={() => {
                setCurrentDisplayedId(track.id)
              }}
              className="px-2 h-5 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:opacity-90 flex items-center gap-1"
              title="Load loop in editor below"
            >
              <CodeIcon weight="regular" size={12} />
            </button>
          )}
        </div>
      </div>
      <div className="hidden md:flex items-center justify-end gap-2">
        {parentTrack && (
          <div className="flex items-center justify-between gap-2 shrink-0">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="text-xs text-[#888] shrink-0">Remix of:</div>
              <div className="font-semibold text-sm flex items-center gap-1 min-w-0">
                {parentTrack.userId && (
                  <Link
                    href={`/profile/${parentTrack.userId}`}
                    className="flex items-center gap-1 text-[#888] hover:text-orange-500"
                    title={`Visit ${parentTrack.userName || 'Unknown'}'s profile`}
                  >
                    <ProfileImage
                      userId={parentTrack.userId}
                      userName={parentTrack.userName || 'Unknown'}
                      size={14}
                      hasAvatar={parentTrack.userHasAvatar}
                      className="shrink-0"
                    />
                    <span className="cursor-pointer shrink-0 md:shrink-0 break-words">
                      {parentTrack.userName || 'Unknown'}
                    </span>
                  </Link>
                )}
                <span className="text-xs text-[#888]">-</span>
                <span
                  className="truncate hover:text-orange-500 cursor-pointer"
                  title={`View ${parentTrack.title} by ${parentTrack.userName || 'Unknown'}`}
                >
                  <Link href={`/loop/${parentTrack.id}`}>{parentTrack.title}</Link>
                </span>
              </div>
            </div>
            <div className="flex gap-1 shrink-0" onClick={e => e.stopPropagation()}>
              {activeRemixId === parentTrack.id
                ? (
                  <button
                    onClick={onStopRemix}
                    className="px-2 h-5 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:bg-neutral-800 flex items-center"
                    title="Stop"
                  >
                    <StopIcon weight="fill" size={12} />
                  </button>
                )
                : (
                  <button
                    onClick={() => {
                      onPlayRemix({ ...parentTrack, code: parentTrack.code })
                    }}
                    className="px-2 h-5 bg-gradient-to-br from-orange-400 to-red-600 text-white rounded hover:opacity-90 flex items-center"
                    title="Play"
                  >
                    <PlayIcon weight="fill" size={12} />
                  </button>
                )}
              {channelMode === 'dual' && onLoadInA && onLoadInB
                ? (
                  <>
                    <button
                      onClick={() => {
                        const codeFile = getCodeFile(parentTrack.id, parentTrack.code)
                        onLoadInA({ ...parentTrack, code: codeFile.value })
                      }}
                      className="pr-2.5 pl-1.5 h-5 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 flex items-center justify-center gap-0"
                      title="Load in A"
                    >
                      <CaretLeftIcon weight="regular" size={12} />
                      <span className="text-[8px] leading-none -ml-0.5 relative top-[1px]">A</span>
                    </button>
                    <button
                      onClick={() => {
                        const codeFile = getCodeFile(parentTrack.id, parentTrack.code)
                        onLoadInB({ ...parentTrack, code: codeFile.value })
                      }}
                      className="pl-2.5 pr-1.5 h-5 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 flex items-center justify-center gap-0"
                      title="Load in B"
                    >
                      <span className="text-[8px] leading-none -mr-0.5 relative top-[1px]">B</span>
                      <CaretRightIcon weight="regular" size={12} />
                    </button>
                  </>
                )
                : (
                  <button
                    onClick={() => {
                      const codeFile = getCodeFile(parentTrack.id, parentTrack.code)
                      onLoad({ ...parentTrack, code: codeFile.value })
                    }}
                    className="px-2 h-5 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 flex items-center"
                    title="Load loop in editor"
                  >
                    <CaretLeftIcon weight="regular" size={12} />
                  </button>
                )}
              <button
                onClick={() => {
                  setCurrentDisplayedId(parentTrack.id)
                }}
                className="px-2 h-5 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:opacity-90 flex items-center"
                title="Load loop in editor below"
              >
                <CodeIcon weight="regular" size={12} />
              </button>
              {!hasNoAccount && parentTrack.userId !== user?.id && (
                <button
                  onClick={() => {
                    if (!user) {
                      onOpenAuthModal?.(parentTrack)
                    }
                    else {
                      onToggleLike(parentTrack)
                    }
                  }}
                  className="px-2 h-5 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:opacity-90 flex items-center gap-1"
                  title={likedTrackIds.has(parentTrack.id) ? 'Unlike' : 'Like'}
                >
                  <HeartIcon
                    weight="fill"
                    size={12}
                    className={likedTrackIds.has(parentTrack.id) ? 'text-red-500' : 'text-neutral-600'}
                  />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="border-b border-b-[#333] bg-black">
      <InlineEditor
        codeFile={displayedCodeFile}
        errors={editorErrors}
        errorMessage={error}
        isPlaying={isPlaying}
        isActive={isActive}
        actuallyPlaying={actuallyPlaying}
        compileMode={compileMode}
        onCompile={handleCompile}
        onCompileCheck={handleCompileCheck}
        showPlayStop={true}
        showVisualizer={true}
        onPlay={handlePlay}
        onStop={handleStop}
        headerContent={headerContent}
        autoHeight={!showTitle}
        height={showTitle ? '300px' : undefined}
        onErrorsChange={setEditorErrors}
        onErrorMessageChange={setError}
        patternEvents={patternEvents}
        sequences={sequences}
        numbersWithParams={numbersWithParams}
        amplitudeInfos={amplitudeInfos}
        waveformInfos={waveformInfos}
        levelInfos={levelInfos}
        timelineInfos={timelineInfos}
        contextId={currentDisplayedId}
      />

      {showRemixes && remixes.length > 0 && (
        <div className="p-3 border border-[#333] border-t-0">
          <div className="space-y-1">
            {remixes.map(remix => (
              <div key={remix.id} className="flex items-center gap-2 rounded">
                <div className="flex gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                  {activeRemixId === remix.id
                    ? (
                      <button
                        onClick={onStopRemix}
                        className="px-2 h-5 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:bg-neutral-800 flex items-center"
                        title="Stop"
                      >
                        <StopIcon weight="fill" size={12} />
                      </button>
                    )
                    : (
                      <button
                        onClick={() => {
                          onPlayRemix({ ...remix, code: remix.code })
                        }}
                        className="px-2 h-5 bg-gradient-to-br from-orange-400 to-red-600 text-white rounded hover:opacity-90 flex items-center"
                        title="Play"
                      >
                        <PlayIcon weight="fill" size={12} />
                      </button>
                    )}
                  <div className="hidden md:flex gap-1">
                    {channelMode === 'dual' && onLoadInA && onLoadInB
                      ? (
                        <>
                          <button
                            onClick={() => {
                              const codeFile = getCodeFile(remix.id, remix.code)
                              onLoadInA({ ...remix, code: codeFile.value })
                            }}
                            className="pr-2.5 pl-1.5 h-5 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 flex items-center justify-center gap-0"
                            title="Load in A"
                          >
                            <CaretLeftIcon weight="regular" size={12} />
                            <span className="text-[8px] leading-none -ml-0.5 relative top-[1px]">
                              A
                            </span>
                          </button>
                          <button
                            onClick={() => {
                              const codeFile = getCodeFile(remix.id, remix.code)
                              onLoadInB({ ...remix, code: codeFile.value })
                            }}
                            className="pl-2.5 pr-1.5 h-5 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 flex items-center justify-center gap-0"
                            title="Load in B"
                          >
                            <span className="text-[8px] leading-none -mr-0.5 relative top-[1px]">
                              B
                            </span>
                            <CaretRightIcon weight="regular" size={12} />
                          </button>
                        </>
                      )
                      : (
                        <button
                          onClick={() => {
                            const codeFile = getCodeFile(remix.id, remix.code)
                            onLoad({ ...remix, code: codeFile.value })
                          }}
                          className="px-2 h-5 bg-gradient-to-br from-neutral-500 to-neutral-700 text-white rounded hover:opacity-90 flex items-center"
                          title="Load loop in editor"
                        >
                          <CaretLeftIcon weight="regular" size={12} />
                        </button>
                      )}
                    <button
                      onClick={() => {
                        setCurrentDisplayedId(remix.id)
                      }}
                      className="px-2 h-5 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:opacity-90 flex items-center"
                      title="Load loop in editor below"
                    >
                      <CodeIcon weight="regular" size={12} />
                    </button>
                  </div>
                  {!hasNoAccount && remix.userId !== user?.id && (
                    <button
                      onClick={() => {
                        if (!user) {
                          onOpenAuthModal?.(remix)
                        }
                        else {
                          onToggleLike(remix)
                        }
                      }}
                      className="px-2 h-5 bg-gradient-to-br from-neutral-700 to-neutral-900 text-white rounded hover:opacity-90 flex items-center gap-1"
                      title={likedTrackIds.has(remix.id) ? 'Unlike' : 'Like'}
                    >
                      <HeartIcon
                        weight="fill"
                        size={12}
                        className={likedTrackIds.has(remix.id) ? 'text-red-500' : 'text-neutral-600'}
                      />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-1 min-w-0">
                    {remix.userId && (
                      <ProfileImage
                        userId={remix.userId}
                        userName={remix.userName || 'Unknown'}
                        size={14}
                        hasAvatar={remix.userHasAvatar}
                        className="shrink-0"
                      />
                    )}
                    <span
                      className="text-[#888] hover:text-orange-500 cursor-pointer shrink-0"
                      title={`Visit ${remix.userName || 'Unknown'}'s profile`}
                    >
                      <Link href={`/profile/${remix.userId}`}>{remix.userName || 'Unknown'}</Link>
                    </span>
                    <span className="text-[#888]">-</span>
                    <span
                      className="truncate hover:text-orange-500 cursor-pointer"
                      title={`View ${remix.title} by ${remix.userName || 'Unknown'}`}
                    >
                      <Link href={`/loop/${remix.id}`}>{remix.title}</Link>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showComments && (
        <div className="border-t border-[#333] bg-[#0a0a0a]">
          <div className="p-4">
            <h3 className="text-sm font-semibold mb-3">Comments</h3>
            <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
              {loadingComments
                ? (
                  <div className="flex items-center justify-center gap-2 text-[#888]">
                    <div className="w-4 h-4">
                      <Spinner />
                    </div>
                    <span className="text-xs italic">Loading comments...</span>
                  </div>
                )
                : !comments || comments.length === 0
                ? (
                  <div className="text-xs text-[#888] italic">
                    No comments yet - Be the first to leave a comment!
                  </div>
                )
                : (
                  <>
                    {comments.length > 2 && (
                      <button
                        onClick={() => setCommentsExpanded(!commentsExpanded)}
                        className="flex items-center gap-1 text-xs text-[#888] hover:text-orange-500 cursor-pointer mb-2"
                      >
                        <span>{commentsExpanded ? 'Less' : 'More'}</span>
                        {commentsExpanded
                          ? <CaretUpIcon weight="regular" size={12} />
                          : <CaretDownIcon weight="regular" size={12} />}
                      </button>
                    )}
                    {[...comments]
                      .sort(
                        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
                      )
                      .slice(commentsExpanded ? 0 : -2)
                      .map(comment => (
                        <div key={comment.id} className="flex gap-2 items-start">
                          <ProfileImage
                            userId={comment.userId}
                            userName={comment.userName || 'Unknown'}
                            size={20}
                            hasAvatar={comment.userHasAvatar}
                            className="shrink-0 mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="text-xs font-semibold text-[#aaa]">
                                <Link href={`/profile/${comment.userId}`}>
                                  {comment.userName || 'Unknown'}
                                </Link>
                              </span>
                              <span className="text-[10px] text-[#666]">
                                {formatRelativeTime(new Date(comment.createdAt))}
                              </span>
                            </div>
                            <p className="text-sm text-white mt-1 break-words">{comment.text}</p>
                          </div>
                          {user && (comment.userId === user.id || track.userId === user.id) && (
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="p-1 hover:bg-[#1a1a1a] rounded shrink-0"
                              title="Delete comment"
                            >
                              <TrashIcon
                                weight="regular"
                                size={14}
                                className="text-[#666] hover:text-red-500"
                              />
                            </button>
                          )}
                        </div>
                      ))}
                  </>
                )}
            </div>
            {user
              ? (
                <div className="flex gap-2">
                  <input
                    ref={commentInputRef}
                    type="text"
                    placeholder="Add a comment..."
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handlePostComment()
                      }
                    }}
                    disabled={postingComment}
                    className="flex-1 px-3 py-2 bg-[#1a1a1a] border border-[#333] rounded text-sm text-white placeholder-[#666] focus:outline-none focus:border-orange-500 disabled:opacity-50"
                  />
                  <button
                    onClick={handlePostComment}
                    disabled={!commentText.trim() || postingComment}
                    className="px-4 py-2 bg-gradient-to-br from-orange-400 to-red-600 text-white rounded hover:opacity-90 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {postingComment ? 'Posting...' : 'Comment'}
                  </button>
                </div>
              )
              : (
                !hasNoAccount && (
                  <div className="flex">
                    <button
                      onClick={() => onOpenAuthModal?.()}
                      className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white hover:opacity-90 flex items-center gap-2"
                    >
                      Sign In to Comment
                    </button>
                  </div>
                )
              )}
          </div>
        </div>
      )}
    </div>
  )
}

export interface TrackListProps {
  tracks: Track[]
  loading: boolean
  header: React.ReactNode
  showTitle?: boolean
  activeTrackId: string | null
  actuallyPlaying: boolean
  trackParents: Record<string, Track>
  trackRemixes: Record<string, Track[]>
  likedTrackIds: Set<string>
  activeRemixId: string | null
  compileMode: 'key' | 'ctrl+enter'
  hasMore?: boolean
  loadingMore?: boolean
  onSetActiveTrackId: (id: string | null) => void
  onSetActiveRemixId: (id: string | null) => void
  onSetActuallyPlaying: (playing: boolean) => void
  onLoad: (track: Track) => void
  onLoadInA?: (track: Track) => void
  onLoadInB?: (track: Track) => void
  onToggleLike: (track: Track) => void
  onPlayRemix: (remix: Track) => void
  onStopRemix: () => void
  onLoadMore?: () => void
  expandCommentsByDefault?: boolean
  onOpenAuthModal?: (track?: Track) => void
}

export function TrackList({
  tracks,
  loading,
  header,
  showTitle = true,
  activeTrackId,
  actuallyPlaying,
  trackParents,
  trackRemixes,
  likedTrackIds,
  activeRemixId,
  compileMode,
  hasMore = false,
  loadingMore = false,
  onSetActiveTrackId,
  onSetActiveRemixId,
  onSetActuallyPlaying,
  onLoad,
  onLoadInA,
  onLoadInB,
  onToggleLike,
  onPlayRemix,
  onStopRemix,
  onLoadMore,
  expandCommentsByDefault = false,
  onOpenAuthModal,
}: TrackListProps) {
  const { channelMode } = useChannelModeStore()
  const {
    setPublicTracksActiveTrackId,
    removePublicTracksActiveRemixId,
    removePublicTracksActiveTrackId,
  } = useSessionStore()
  const [hasInitialLoad, setHasInitialLoad] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loaderElementRef = useRef<HTMLDivElement | null>(null)
  const onLoadMoreRef = useRef(onLoadMore)
  const hasMoreRef = useRef(hasMore)
  const loadingMoreRef = useRef(loadingMore)
  const isTriggeredRef = useRef(false)

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])

  useEffect(() => {
    loadingMoreRef.current = loadingMore
    if (!loadingMore) {
      isTriggeredRef.current = false
    }
  }, [loadingMore])

  useEffect(() => {
    if (!loading && tracks.length > 0 && !hasInitialLoad) {
      setHasInitialLoad(true)
    }
  }, [loading, tracks.length, hasInitialLoad])

  const scrollContainerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!hasInitialLoad) return

    const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null
      let current: HTMLElement | null = element.parentElement
      while (current) {
        const style = window.getComputedStyle(current)
        if (
          style.overflowY === 'auto'
          || style.overflowY === 'scroll'
          || style.overflow === 'auto'
          || style.overflow === 'scroll'
        ) {
          return current
        }
        current = current.parentElement
      }
      return null
    }

    const scrollContainer = loaderElementRef.current
      ? findScrollContainer(loaderElementRef.current)
      : null

    scrollContainerRef.current = scrollContainer

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (
          entry.isIntersecting
          && hasMoreRef.current
          && !loadingMoreRef.current
          && !isTriggeredRef.current
          && onLoadMoreRef.current
        ) {
          isTriggeredRef.current = true
          onLoadMoreRef.current()
        }
      },
      { rootMargin: '1000px', root: scrollContainer },
    )

    observerRef.current = observer

    if (loaderElementRef.current) {
      observer.observe(loaderElementRef.current)
    }

    return () => {
      observer.disconnect()
      observerRef.current = null
    }
  }, [hasInitialLoad])

  const loaderRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (loaderElementRef.current && observerRef.current) {
        observerRef.current.unobserve(loaderElementRef.current)
      }

      loaderElementRef.current = element

      if (!element || !hasInitialLoad) {
        return
      }

      const findScrollContainer = (el: HTMLElement): HTMLElement | null => {
        let current: HTMLElement | null = el.parentElement
        while (current) {
          const style = window.getComputedStyle(current)
          if (
            style.overflowY === 'auto'
            || style.overflowY === 'scroll'
            || style.overflow === 'auto'
            || style.overflow === 'scroll'
          ) {
            return current
          }
          current = current.parentElement
        }
        return null
      }

      const scrollContainer = findScrollContainer(element)
      scrollContainerRef.current = scrollContainer

      if (observerRef.current) {
        observerRef.current.disconnect()
      }

      observerRef.current = new IntersectionObserver(
        entries => {
          const entry = entries[0]
          if (
            entry.isIntersecting
            && hasMoreRef.current
            && !loadingMoreRef.current
            && !isTriggeredRef.current
            && onLoadMoreRef.current
          ) {
            isTriggeredRef.current = true
            onLoadMoreRef.current()
          }
        },
        { rootMargin: '200px', root: scrollContainer },
      )

      observerRef.current.observe(element)
    },
    [hasInitialLoad],
  )

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto flex flex-col gap-10">
        <h2 className="-mb-2 mt-2">{header}</h2>
        <div className="text-center py-12 text-[#888] flex flex-col items-center gap-3">
          <div className="w-6 h-6">
            <Spinner />
          </div>
          <span>Loading loops...</span>
        </div>
      </div>
    )
  }

  if (tracks.length === 0) {
    return (
      <div className="max-w-4xl mx-auto flex flex-col gap-10">
        <h2 className="-mb-2 mt-2">{header}</h2>
        <div className="text-center py-12 text-[#888]">No loops yet</div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto flex flex-col gap-10">
      <h2 className="-mb-2 mt-2">{header}</h2>
      {tracks.map(track => (
        <TrackPlayer
          key={track.id}
          showTitle={showTitle}
          track={track}
          isActive={activeTrackId === track.id}
          actuallyPlaying={activeTrackId === track.id ? actuallyPlaying : false}
          onPlay={() => {
            onSetActiveTrackId(track.id)
            onSetActiveRemixId(null)
            setPublicTracksActiveTrackId(track.id)
            removePublicTracksActiveRemixId()
          }}
          onStop={() => {
            onSetActiveTrackId(null)
            removePublicTracksActiveTrackId()
          }}
          onPlayingChange={onSetActuallyPlaying}
          onLoad={onLoad}
          onLoadInA={onLoadInA}
          onLoadInB={onLoadInB}
          onToggleLike={onToggleLike}
          isLiked={likedTrackIds.has(track.id)}
          parentTrack={trackParents[track.id]}
          remixes={trackRemixes[track.id] || []}
          onPlayRemix={onPlayRemix}
          onStopRemix={onStopRemix}
          activeRemixId={activeRemixId}
          likedTrackIds={likedTrackIds}
          channelMode={channelMode}
          compileMode={compileMode}
          expandCommentsByDefault={expandCommentsByDefault}
          onOpenAuthModal={onOpenAuthModal}
        />
      ))}
      {hasInitialLoad && hasMore && (
        <div
          ref={loaderRef}
          className="text-center py-8 text-[#888] flex flex-col items-center gap-3"
        >
          {loadingMore
            ? (
              <>
                <div className="w-5 h-5">
                  <Spinner />
                </div>
                <span>Loading more loops...</span>
              </>
            )
            : <div style={{ height: '1px', width: '100%' }} />}
        </div>
      )}
      {hasInitialLoad && !hasMore && !loadingMore && (
        <div className="text-center pt-4 pb-5 text-[#888] flex items-center justify-center gap-3">
          <CodeIcon size={20} />
        </div>
      )}
    </div>
  )
}
